import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import SelectMenuItems from '@/components-latest/ui/OfferPage/OfferDetailsForm/SelectMenuItems'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { useSubmitOfferMutation } from '@/ducks/offers/api/offers.api'

import ChooseOfferDetails from './ChooseOfferDetails'
import Styles from './OfferDetailsForm.module.scss'

type Props = {
  onSuccess: () => void
  /** Defaults to {@link onSuccess} when omitted (backward compatible). */
  onGoBack?: () => void
  offerId?: string
  initialDiscount?: number
  initialExpiration?: string
  name?: string
}

const OfferDetailsForm = React.memo(
  ({
    onSuccess,
    onGoBack,
    offerId,
    initialDiscount,
    initialExpiration,
    name
  }: Props) => {
    const handleGoBack = onGoBack ?? onSuccess
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [submitOffer, { isLoading }] = useSubmitOfferMutation()

    const mode = offerId ? 'edit' : 'create'
    const [chosenMenuItemsIds, setChosenMenuItemsIds] = useState<string[]>(
      offerId ? [offerId] : []
    )

    const [discount, setDiscount] = useState(
      initialDiscount ? String(initialDiscount) : ''
    )
    const onDiscountChange = (value: string) => {
      setDiscount(value)
    }

    const [expiration, setExpiration] = useState<string>(
      initialExpiration || ''
    )
    const onExpirationChange = (value: string) => {
      setExpiration(value)
    }

    const isSubmitButtonDisabled =
      chosenMenuItemsIds.length === 0 || !discount || !expiration

      const onOfferSubmit = () => {
        if (isSubmitButtonDisabled) return

        submitOffer({
          ...(offerId && { id: offerId }),
          offerValidUntil: expiration,
          amount: Number(discount),
          foodIds: chosenMenuItemsIds
        })
          .unwrap()
          .then(({ message }) => {
            dispatch(
              addSystemNotification({
                message:
                  mode === 'create'
                    ? t('offers.offers-creation.success')
                    : t('offers.offers-edition.success'),
                intent: 'success'
              })
            )
            onSuccess()
          })
          .catch(e => {
            dispatch(
              addSystemNotification({
                message:
                  e?.message ||
                  (mode === 'create'
                    ? t('offers.offers-creation.error')
                    : t('offers.offers-edition.error')),
                intent: 'error'
              })
            )
          })
    }

    return (
      <>
        <Header
          title={
            mode === 'create'
              ? t('offers.create-offer-title')
              : t('offers.edit-offer-title', { name })
          }
          onGoBack={handleGoBack}>
          <Button
            disabled={isSubmitButtonDisabled || isLoading}
            loading={isLoading}
            onClick={onOfferSubmit}
            shape="rounded"
            variant="branded">
            {t('offers.offers-creation.save')}
          </Button>
        </Header>

        <div
          key={offerId ?? 'create'}
          className={Styles.content}
        >
          {!offerId && (
            <SelectMenuItems
              chosenMenuItemsIds={chosenMenuItemsIds}
              setChosenMenuItemsIds={setChosenMenuItemsIds}
            />
          )}
          <ChooseOfferDetails
            discountValue={discount}
            expirationValue={expiration}
            onDiscountValueChange={onDiscountChange}  
            onExpirationChange={onExpirationChange}  
          />
        </div>
      </>
    )
  }
)

OfferDetailsForm.displayName = 'OfferDetailsForm'
export default OfferDetailsForm
